import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { asPlaceholder } from "../../i18n/filterLabels";
import TitleWithIcon from "../../components/layout/pageTitle/PageTitle";
import historyLogs from "../../media/history_transaction.png";
import { Col, Container, Row, Table } from "react-bootstrap";
import { Button } from "../../components/ui";
import BlockTitle from "../../components/layout/blockTitle/BlockTitle";
import DateInput from "../../components/ui/DateInput";
import GetData from "../../services/getData";
import filterIcon from "../../media/filter_icon.png";

function LogsHistory() {
    const { t } = useTranslation("settings");
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [filters, setFilters] = useState({
        type: "all",
        userId: "all",
        tableName: "all",
    });
    const [searchTerm, setSearchTerm] = useState("");
    const [dateFrom, setDateFrom] = useState("");
    const [dateTo, setDateTo] = useState("");
    const [allUsers, setAllUsers] = useState([]);
    const [availableTables, setAvailableTables] = useState([]);
    const [isFiltersActive, setIsFiltersActive] = useState(false);
    const [pagination, setPagination] = useState({
        page: 1,
        limit: 50,
        total: 0,
        pages: 1,
    });

    const loadLogs = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await GetData({
                module: "settings",
                block: "logs",
                type: filters.type === "all" ? "" : filters.type,
                userId: filters.userId === "all" ? "" : filters.userId,
                tableName: filters.tableName === "all" ? "" : filters.tableName,
                search: searchTerm.trim(),
                dateFrom: dateFrom,
                dateTo: dateTo,
                page: pagination.page,
                limit: pagination.limit,
            });

            setLogs(data.result || []);
            setAllUsers(data.all_users || []);
            setAvailableTables(data.available_tables || []);
            if (data.pagination) {
                setPagination((prev) => ({
                    ...prev,
                    page: data.pagination.page ?? prev.page,
                    limit: data.pagination.limit ?? prev.limit,
                    total: data.pagination.total ?? prev.total,
                    pages: data.pagination.pages ?? prev.pages,
                }));
            }
        } catch (err) {
            console.error("Ошибка:", err);
            setError(t("logs.errors.loadFailed"));
        } finally {
            setLoading(false);
        }
    }, [filters.type, filters.userId, filters.tableName, searchTerm, dateFrom, dateTo, pagination.page, pagination.limit, t]);

    useEffect(() => {
        document.title = t("logs.title");
        loadLogs();
    }, [loadLogs, t]);

    const handleFilterChange = (filterType, value) => {
        setFilters((prev) => ({ ...prev, [filterType]: value }));
        setPagination((prev) => ({ ...prev, page: 1 }));
    };

    // Есть ли выбранный фильтр: подсвечиваем такие поля, кнопки «Сбросить» больше нет.
    const hasActiveFilters =
        searchTerm.trim() !== "" ||
        filters.type !== "all" ||
        filters.userId !== "all" ||
        filters.tableName !== "all" ||
        dateFrom !== "" ||
        dateTo !== "";

    const handleSearchChange = (e) => {
        setSearchTerm(e.target.value);
        setPagination((prev) => ({ ...prev, page: 1 }));
    };

    const totalPages =
        pagination.pages ||
        (pagination.total && pagination.limit
            ? Math.ceil(pagination.total / pagination.limit)
            : 1);

    const handlePageClick = (pageNumber) => {
        setPagination((prev) => ({ ...prev, page: pageNumber }));
    };

    const renderPageButtons = () => {
        if (totalPages <= 1) return null;
        if (totalPages < 5) {
            return Array.from({ length: totalPages }, (_, idx) => {
                const pageNum = idx + 1;
                return (
                    <Button
                        key={pageNum}
                        variant={pageNum === pagination.page ? "primary" : "outline-secondary"}
                        size="sm"
                        className="mx-1"
                        onClick={() => handlePageClick(pageNum)}>
                        {pageNum}
                    </Button>
                );
            });
        }
        const currentPage = pagination.page;
        let start = Math.max(1, currentPage - 1);
        let end = start + 2;
        if (end > totalPages) {
            end = totalPages;
            start = end - 2;
        }
        const buttons = [];
        if (start > 1) {
            buttons.push(
                <Button
                    key="left-ellipsis"
                    variant="outline-secondary"
                    size="sm"
                    className="mx-1"
                    onClick={() => handlePageClick(start - 1)}>
                    ...
                </Button>
            );
        }
        for (let p = start; p <= end; p++) {
            buttons.push(
                <Button
                    key={p}
                    variant={p === currentPage ? "primary" : "outline-secondary"}
                    size="sm"
                    className="mx-1"
                    onClick={() => handlePageClick(p)}>
                    {p}
                </Button>
            );
        }
        if (end < totalPages) {
            buttons.push(
                <Button
                    key="right-ellipsis"
                    variant="outline-secondary"
                    size="sm"
                    className="mx-1"
                    onClick={() => handlePageClick(end + 1)}>
                    ...
                </Button>
            );
        }
        return buttons;
    };

    const formatText = (text, maxLength = 100) => {
        if (!text) return "";
        if (text.length <= maxLength) return text;
        return text.substring(0, maxLength) + "...";
    };

    const getOperationTypeClass = (type) => {
        switch (parseInt(type)) {
            case 1: return "status-active";
            case 2: return "status-limited";
            case 3: return "status-inactive";
            default: return "status-inactive";
        }
    };

    const getOperationTypeLabel = (type) => {
        switch (parseInt(type)) {
            case 1: return t("logs.opTypes.create");
            case 2: return t("logs.opTypes.update");
            case 3: return t("logs.opTypes.delete");
            default: return t("logs.opTypes.unknown");
        }
    };

    return (
        <Container fluid="md">
            <Row>
                <Col>
                    <BlockTitle
                        title={
                            <TitleWithIcon
                                icon={historyLogs}
                                title={t("logs.title")}
                                showStatus={false}
                                status={""}
                            />
                        }
                        buttons={[]}
                    />
                </Col>
            </Row>
            <Row>
                <Col>
                    <div className="page">
                        <div className="page-content">
                            <div className="page-block">
                                <div
                                    className={`filters-panel ${
                                        isFiltersActive ? "active" : ""
                                    } ${hasActiveFilters ? "has-active-filters" : ""}`}
                                >
                                    <div className="filters-panel-title">
                                        <div className="filters-panel-title-icon">
                                            <img
                                                className="title-with-icon__image"
                                                src={filterIcon}
                                                alt={t("filters.alt", { ns: "common" })}
                                                title={t("filters.alt", { ns: "common" })}
                                            />
                                            <div className="filters-panel-name">
                                                {t("filters.title", { ns: "common" })}
                                            </div>
                                        </div>
                                        <Button
                                            onClick={() => setIsFiltersActive((prev) => !prev)}
                                            size="sm"
                                            variant="secondary">
                                            {isFiltersActive
                                                ? t("filters.hide", { ns: "common" })
                                                : t("filters.show", { ns: "common" })}
                                        </Button>
                                    </div>
                                    <div className="search-section">
                                        <input
                                            type="text"
                                            placeholder={t("logs.searchPlaceholder")}
                                            value={searchTerm}
                                            onChange={handleSearchChange}
                                            className={`search-input ${searchTerm.trim() ? "is-filter-active" : ""}`}
                                        />
                                    </div>
                                    <div className="filters-section">
                                        <div
                                            className={`filter-group ${
                                                filters.type !== "all" ? "is-filter-active" : ""
                                            }`}
                                        >
                                            <select
                                                className={
                                                    filters.type === "all"
                                                        ? "is-placeholder"
                                                        : ""
                                                }
                                                value={filters.type}
                                                onChange={(e) =>
                                                    handleFilterChange("type", e.target.value)
                                                }>
                                                <option value="all">
                                                    {asPlaceholder(t("logs.typeLabel"))}
                                                </option>
                                                <option value="1">{t("logs.opTypes.create")}</option>
                                                <option value="2">{t("logs.opTypes.update")}</option>
                                                <option value="3">{t("logs.opTypes.delete")}</option>
                                            </select>
                                        </div>
                                        <div
                                            className={`filter-group ${
                                                filters.userId !== "all" ? "is-filter-active" : ""
                                            }`}
                                        >
                                            <select
                                                className={
                                                    filters.userId === "all"
                                                        ? "is-placeholder"
                                                        : ""
                                                }
                                                value={filters.userId}
                                                onChange={(e) =>
                                                    handleFilterChange("userId", e.target.value)
                                                }>
                                                <option value="all">
                                                    {asPlaceholder(t("logs.userLabel"))}
                                                </option>
                                                {allUsers.map((user) => (
                                                    <option key={user.id} value={user.id}>
                                                        {user.name || user.login || t("logs.userFallback", { id: user.id })}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                        <div
                                            className={`filter-group ${
                                                filters.tableName !== "all" ? "is-filter-active" : ""
                                            }`}
                                        >
                                            <select
                                                className={
                                                    filters.tableName === "all"
                                                        ? "is-placeholder"
                                                        : ""
                                                }
                                                value={filters.tableName}
                                                onChange={(e) =>
                                                    handleFilterChange("tableName", e.target.value)
                                                }>
                                                <option value="all">
                                                    {asPlaceholder(t("logs.tableLabel"))}
                                                </option>
                                                {availableTables.map((table) => (
                                                    <option key={table} value={table}>
                                                        {table}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                        <div
                                            className={`filter-group ${dateFrom !== "" ? "is-filter-active" : ""}`}
                                        >
                                            <DateInput
                                                value={dateFrom}
                                                hint={asPlaceholder(t("logs.dateFromLabel"))}
                                                onChange={(e) => setDateFrom(e.target.value)}
                                                className="date-input"
                                            />
                                        </div>
                                        <div
                                            className={`filter-group ${dateTo !== "" ? "is-filter-active" : ""}`}
                                        >
                                            <DateInput
                                                value={dateTo}
                                                hint={asPlaceholder(t("logs.dateToLabel"))}
                                                onChange={(e) => setDateTo(e.target.value)}
                                                className="date-input"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="page-block table-list">
                                {error ? (
                                    <div className="text-danger">
                                        {t("error", { ns: "common", message: error })}
                                    </div>
                                ) : loading ? (
                                    <div>{t("state.loading", { ns: "common" })}</div>
                                ) : logs.length === 0 ? (
                                    <div className="no-patients">
                                        {t("logs.notFound")}
                                    </div>
                                ) : (
                                    <Table className="main-table-list rounded-table">
                                        <thead>
                                            <tr>
                                                <th>{t("logs.colDate")}</th>
                                                <th>{t("logs.colUser")}</th>
                                                <th>{t("logs.colOp")}</th>
                                                <th>{t("logs.colTable")}</th>
                                                <th>{t("logs.colIdItem")}</th>
                                                <th>{t("logs.colText")}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {logs.map((log) => (
                                                <tr key={log.id}>
                                                    <td>{log.date || ""}</td>
                                                    <td>{log.user_name || t("logs.userFallback", { id: log.user_id })}</td>
                                                    <td>
                                                        <span className={`status-badge ${getOperationTypeClass(log.type_code)}`}>
                                                            {getOperationTypeLabel(log.type_code)}
                                                        </span>
                                                    </td>
                                                    <td className="text-truncate">
                                                        {log.table_name || ""}
                                                    </td>
                                                    <td>{log.id_item > 0 ? log.id_item : "—"}</td>
                                                    <td title={log.full_text || ""}>
                                                        {formatText(log.text)}
                                                    </td>
                                                </tr>
                                            ))}
                                            <tr className="table-summary">
                                                <td colSpan={6} className="summary-cell">
                                                    {t("logs.found", { count: pagination.total || logs.length })}
                                                </td>
                                            </tr>
                                        </tbody>
                                    </Table>
                                )}
                                {logs.length > 0 && totalPages > 1 && (
                                    <div className="pagination-controls mt-3 d-flex justify-content-between align-items-center">
                                        <span>
                                            {t("pageOf", { ns: "common", page: pagination.page, total: totalPages })}
                                        </span>
                                        <div>{renderPageButtons()}</div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </Col>
            </Row>
        </Container>
    );
}

export default LogsHistory;
